// src/api/settingsApi.js
import axiosInstance from './axiosConfig';

export const settingsApi = {
  // ============================================================
  // PUBLIC SETTINGS
  // ============================================================
  getPublicSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings/public');
      return response.data;
    } catch (error) {
      console.error('Error fetching public settings:', error);
      throw error;
    }
  },

  getSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  updateSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/settings', data);
      return response.data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  // ============================================================
  // LOGO UPLOAD
  // ============================================================
  uploadLogo: async (formData) => {
    try {
      const response = await axiosInstance.post('/settings/admin/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  },

  // ============================================================
  // ADMIN SETTINGS
  // ============================================================
  getAdminSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings/admin');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin settings:', error);
      throw error;
    }
  },

  updateAdminSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/settings/admin', data);
      return response.data;
    } catch (error) {
      console.error('Error updating admin settings:', error);
      throw error;
    }
  },

  // ============================================================
  // THEME SETTINGS
  // ============================================================
  getThemeSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings/theme');
      return response.data;
    } catch (error) {
      console.error('Error fetching theme settings:', error);
      throw error;
    }
  },

  updateThemeSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/settings/theme', data);
      return response.data;
    } catch (error) {
      console.error('Error updating theme settings:', error);
      throw error;
    }
  },

  // ============================================================
  // VENDOR MANAGEMENT
  // ============================================================
  getVendors: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/admin/vendors', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  },

  updateVendorCommission: async (data) => {
    try {
      const response = await axiosInstance.put('/settings/admin/vendor-commission', data);
      return response.data;
    } catch (error) {
      console.error('Error updating vendor commission:', error);
      throw error;
    }
  },

  // ============================================================
  // PAYMENT SETTINGS
  // ============================================================
  getSiinqeeSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings/admin/payment');
      return response.data;
    } catch (error) {
      console.error('Error fetching Siinqee settings:', error);
      throw error;
    }
  },

  updateSiinqeeSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/settings/admin/payment', data);
      return response.data;
    } catch (error) {
      console.error('Error updating Siinqee settings:', error);
      throw error;
    }
  },

  getChapaSettings: async () => {
    try {
      const response = await axiosInstance.get('/settings/admin/payment');
      return response.data;
    } catch (error) {
      console.error('Error fetching Chapa settings:', error);
      throw error;
    }
  },

  updateChapaSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/settings/admin/payment', data);
      return response.data;
    } catch (error) {
      console.error('Error updating Chapa settings:', error);
      throw error;
    }
  },

  // ============================================================
  // LOGISTICS
  // ============================================================
  getLogisticsPricing: async () => {
    try {
      const response = await axiosInstance.get('/logistics/pricing/active');
      return response.data;
    } catch (error) {
      console.error('Error fetching logistics pricing:', error);
      throw error;
    }
  },

  // ============================================================
  // ✅ TRANSACTIONS (Admin)
  // ============================================================
  getAdminTransactions: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/admin/transactions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin transactions:', error);
      throw error;
    }
  },

  getAdminTransactionDetails: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/admin/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      throw error;
    }
  },

  updateTransactionStatus: async (transactionId, status, adminNotes = '') => {
    try {
      const response = await axiosInstance.patch(`/admin/transactions/${transactionId}`, {
        status,
        adminNotes,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating transaction status:', error);
      throw error;
    }
  },

  // ============================================================
  // ✅ TRANSACTIONS (Vendor)
  // ============================================================
  getVendorTransactions: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/vendor/transactions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor transactions:', error);
      throw error;
    }
  },

  getVendorTransactionDetails: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/vendor/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor transaction details:', error);
      throw error;
    }
  },

  // ============================================================
  // ✅ HELPER METHODS
  // ============================================================
  getFullLogoUrl: (logoPath) => {
    if (!logoPath) return '';
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.goscitech.com';
    if (logoPath.startsWith('/')) {
      return `${baseUrl}${logoPath}`;
    }
    return `${baseUrl}/${logoPath}`;
  },

  isValidLogoUrl: (url) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  },
};

export default settingsApi;